import { User, UserGroups } from '@/utils/types';

/**
 * Resultado de uma tentativa de resolver a sessão.
 *
 * `unauthenticated` é o backend dizendo explicitamente que não há sessão — só esse caso derruba
 * token. `error` é falha transitória (rede, timeout, 5xx) e nunca pode deslogar ninguém: já bastou
 * um soluço de rede sumir com a ação de recobrança da tela de assinantes sem nenhum sinal na UI.
 */
export type SessionResult =
  | { status: 'authenticated'; user: User }
  | { status: 'unauthenticated' }
  | { status: 'error'; error?: unknown };

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface AuthSessionState {
  user: User | null;
  userLoaded: boolean;
  userData: User | null;
  userGroups: UserGroups[];
  sessionStatus: SessionStatus;
}

export const SESSION_ENDPOINT = '/api/auth/session';
export const SESSION_MAX_ATTEMPTS = 3;
export const SESSION_RETRY_BASE_DELAY_MS = 600;

export const DEFAULT_AUTH_SESSION_STATE: AuthSessionState = {
  user: null,
  userLoaded: false,
  userData: null,
  userGroups: [],
  sessionStatus: 'idle',
};

// A API pode retornar 'user' (singular) mas o enum usa 'users' (plural)
export const normalizeUserRole = (role: string | undefined): UserGroups => {
  if (!role || role === 'user') return 'users' as UserGroups;
  return role as UserGroups;
};

export const getSessionRetryDelay = (attempt: number): number => SESSION_RETRY_BASE_DELAY_MS * 2 ** attempt;

/** 401/403 é resposta autenticada-negativa; o resto de não-OK é indisponibilidade. */
const isExplicitlyUnauthenticatedStatus = (status: number): boolean => status === 401 || status === 403;

export async function requestSession(options?: {
  fetchImpl?: typeof fetch;
  token?: string | null;
}): Promise<SessionResult> {
  const fetchImpl = options?.fetchImpl ?? fetch;
  const headers: Record<string, string> = {};
  if (options?.token) {
    headers['Authorization'] = `Bearer ${options.token}`;
  }

  try {
    const response = await fetchImpl(SESSION_ENDPOINT, { method: 'GET', credentials: 'include', headers });

    if (!response.ok) {
      if (isExplicitlyUnauthenticatedStatus(response.status)) return { status: 'unauthenticated' };
      return { status: 'error', error: new Error(`Session endpoint responded ${response.status}`) };
    }

    const data = await response.json();

    if (data?.authenticated && data?.user) {
      return { status: 'authenticated', user: data.user as User };
    }

    // A rota responde 200 tanto para "não autenticado" quanto para alguns modos de falha antigos;
    // o `reason` é o que separa os dois. Sem `reason`, a resposta OK vale como não autenticado.
    if (data?.reason && data.reason !== 'unauthenticated') {
      return { status: 'error', error: new Error(`Session unavailable: ${data.reason}`) };
    }

    return { status: 'unauthenticated' };
  } catch (error) {
    return { status: 'error', error };
  }
}

/** Repete só a falha transitória; resposta autenticada-negativa volta na primeira tentativa. */
export async function loadSessionWithRetry(options?: {
  request?: () => Promise<SessionResult>;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
  fetchImpl?: typeof fetch;
  token?: string | null;
}): Promise<SessionResult> {
  const request = options?.request ?? (() => requestSession({ fetchImpl: options?.fetchImpl, token: options?.token }));
  const maxAttempts = options?.maxAttempts ?? SESSION_MAX_ATTEMPTS;
  const sleep = options?.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  let result: SessionResult = { status: 'error' };

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    result = await request();
    if (result.status !== 'error') return result;
    if (attempt < maxAttempts - 1) await sleep(getSessionRetryDelay(attempt));
  }

  return result;
}

export function shouldClearSessionTokens(result: SessionResult, preserveTokensOnError?: boolean): boolean {
  if (result.status !== 'unauthenticated') return false;
  return !preserveTokensOnError;
}

/** Falha transitória preserva o usuário já carregado e não marca a sessão como resolvida. */
export function reduceAuthSessionState(previous: AuthSessionState, result: SessionResult): AuthSessionState {
  if (result.status === 'authenticated') {
    return {
      user: result.user,
      userLoaded: true,
      userData: result.user,
      userGroups: [normalizeUserRole(result.user?.role)],
      sessionStatus: 'authenticated',
    };
  }

  if (result.status === 'unauthenticated') {
    return { ...DEFAULT_AUTH_SESSION_STATE, userLoaded: true, sessionStatus: 'unauthenticated' };
  }

  return { ...previous, sessionStatus: 'error' };
}
